import { useEffect, useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'

import { Dialog, Button, Form, Text, Alert } from '@/ui'
import {
  ADMIN_CREATE_USER_IMPORTANT_DATE_KEY,
  adminCreateUserImportantDate,
} from '@/features/user/dashboard/user-important-dates/userImportantDates.slice'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import {
  fetchAllImportantDates,
  selectImportantDates,
} from '@/features/admin/action-items/allActionItems.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

interface UserImportantDateCreationDialogProps {
  open: boolean
  close: () => void
  userId: number
}

export const UserImportantDateCreationDialog = ({
  open,
  close,
  userId,
}: UserImportantDateCreationDialogProps) => {
  const dispatch = useAppDispatch()
  const error = useReselector(
    getFetchError,
    ADMIN_CREATE_USER_IMPORTANT_DATE_KEY
  )

  const allImportantDates = useReselector(selectImportantDates)
  const importantDateOptions = useMemo(
    () =>
      allImportantDates.map((date) => ({
        text: date.description,
        value: date.id,
      })),
    [allImportantDates]
  )

  useEffect(() => {
    fetchAllImportantDates()(dispatch)
  }, [dispatch])

  const validationSchema = yup.object({
    actionItemId: yup.number().required('You must select an important Date.'),
  })

  const formik = useFormik({
    initialValues: {
      actionItemId: 0,
    },
    validationSchema,
    onSubmit: async (actionItemId: { actionItemId: number }) => {
      const res = await adminCreateUserImportantDate(
        userId,
        actionItemId
      )(dispatch)
      if (res) {
        close()
      }
    },
  })

  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Content>
        <div>
          <Text variant={TypographyStyles.H1}>
            Assign an important date for User to complete
          </Text>
          <Text variant={TypographyStyles.BODY_MD} className="my-4">
            This allows you to add an important date for the user to complete.
            This will show up on a user&#39;s Dashboard.
          </Text>
          {error && <Alert variant="error">{error.message}</Alert>}
          <FormikProvider value={formik}>
            <div className="space-y-4">
              <Text variant={TypographyStyles.H3}>Select Important Date</Text>
              <Form.Select name="actionItemId" options={importantDateOptions} />
            </div>
            <Dialog.Footer className="mt-4">
              <Dialog.Close asChild>
                <Button variant="secondary" onClick={close}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button variant="primary" onClick={() => formik.handleSubmit()}>
                Create
              </Button>
            </Dialog.Footer>
          </FormikProvider>
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
